import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../App';

const PRIORITY_COLORS = { high: '#EF5350', medium: '#FF9800', low: '#4CAF50' };
const PRIORITY_LABELS = { high: '🔴 Urgent', medium: '🟡 Normal', low: '🟢 Faible' };
const RECURRENCE_LABELS = {
  une_fois: 'Une fois', quotidienne: 'Chaque jour', hebdomadaire: 'Chaque semaine',
  bimensuelle: 'Toutes les 2 sem.', mensuelle: 'Chaque mois'
};
const TASK_ICONS = ['✅','🗑️','🧹','👕','🍳','🐾','📄','💊','🚗','🛒','📚','🔧','💡','🌿','🎁','🏠','💧','🪴','🐕','🍼'];

export default function Tasks() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [members, setMembers] = useState([]);
  const [filter, setFilter] = useState('tous');
  const [showForm, setShowForm] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [completing, setCompleting] = useState(null);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [showCompleted, setShowCompleted] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    title: '', icon: '✅', category: 'autre', recurrence: 'une_fois',
    next_due: (() => { const d=new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })(), due_time: '', assigned_to: '',
    priority: 'medium', rotation_enabled: false, due_scope: '', recurrence_months: ''
  });

  const load = useCallback(() => {
    api.get('/tasks').then(r => setTasks(r.data)).catch(console.error);
    api.get('/tasks/completed').then(r => setCompletedTasks(r.data)).catch(console.error);
    api.get('/family/members').then(r => setMembers(r.data)).catch(console.error);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { window.addEventListener('family_refresh', load); return () => window.removeEventListener('family_refresh', load); }, [load]);

  const today = (() => { const d=new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })();

  const openNew = () => {
    setForm({ title: '', icon: '✅', category: 'autre', recurrence: 'une_fois', next_due: today, due_time: '', assigned_to: '', priority: 'medium', rotation_enabled: false, due_scope: '', recurrence_months: '' });
    setEditTask(null);
    setShowForm(true);
  };

  const openEdit = (task) => {
    setForm({
      title: task.title, icon: task.icon || '✅', category: task.category || 'autre',
      recurrence: task.recurrence || 'une_fois',
      next_due: task.next_due || '',
      due_time: task.due_time ? task.due_time.substring(0, 5) : '',
      assigned_to: task.assigned_to || '', priority: task.priority || 'medium',
      rotation_enabled: !!task.rotation_enabled,
      due_scope: !task.next_due && !task.due_scope ? 'no_date' : (task.due_scope || ''),
      recurrence_months: task.recurrence_months || ''
    });
    setEditTask(task);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const payload = { ...form, assigned_to: form.assigned_to || null };
      if (editTask) await api.put(`/tasks/${editTask.id}`, payload);
      else await api.post('/tasks', payload);
      setShowForm(false);
      load();
    } catch (e) { alert(e.response?.data?.error || e.message); }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!editTask || !window.confirm('Supprimer cette tâche ?')) return;
    await api.delete(`/tasks/${editTask.id}`);
    setShowForm(false);
    load();
  };

  const handleComplete = async (taskId, taskTitle) => {
    setCompleting(taskId);
    try {
      await api.post(`/tasks/${taskId}/complete`, { user_id: user.id });
      load();
    } catch (e) { alert(e.response?.data?.error || e.message); }
    setCompleting(null);
  };

  const handleUncomplete = async (taskId) => {
    try {
      await api.delete(`/tasks/${taskId}/complete`);
      load();
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  const filtered = tasks.filter(t => {
    if (filter === 'tous') return true;
    if (filter === 'urgent') return t.priority === 'high';
    if (filter === 'aujourd_hui') return t.next_due === today;
    if (filter === 'moi') return t.assigned_to === user.id;
    return true;
  });

  const overdue = filtered.filter(t => t.next_due && t.next_due < today && !t.due_scope && !t.title.toLowerCase().includes('poubelle'));
  const todayTasks = filtered.filter(t => !t.due_scope && t.next_due === today);
  const noDateTasks = filtered.filter(t => !t.next_due && !t.due_scope);

  const localISO = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  const endOfWeek = (() => { const d = new Date(); d.setDate(d.getDate() + (7 - d.getDay())); return localISO(d); })();
  const endOfMonth = localISO(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0));
  const in3months = (() => { const d = new Date(); d.setMonth(d.getMonth() + 3); return localISO(d); })();

  const thisWeek = filtered.filter(t =>
    t.due_scope === 'week' ||
    (!t.due_scope && t.next_due && t.next_due > today && t.next_due <= endOfWeek)
  );
  const thisMonth = filtered.filter(t =>
    t.due_scope === 'month' ||
    (!t.due_scope && t.next_due && t.next_due > today && t.next_due > endOfWeek && t.next_due <= endOfMonth)
  );
  const trimestre = filtered.filter(t =>
    !t.due_scope && t.next_due && t.next_due > today && t.next_due > endOfMonth && t.next_due <= in3months
  );
  const later = filtered.filter(t =>
    !t.due_scope && t.next_due && t.next_due > today && t.next_due > in3months
  );

  const renderTask = (task) => {
    const isOverdue = task.next_due && task.next_due < today;
    const isToday = task.next_due === today || !task.next_due;
    const member = members.find(m => m.id === task.assigned_to);
    return (
      <div key={task.id} className="task-item" style={{ cursor: 'default' }}>
        <div className="task-priority-bar" style={{ background: PRIORITY_COLORS[task.priority] || '#E0E4F0' }} />
        <button className="task-check" onClick={() => handleComplete(task.id, task.title)} disabled={completing === task.id} title="Marquer comme fait" style={{ cursor: 'pointer' }}>
          {completing === task.id ? <span className="spinner" style={{ width: 10, height: 10 }} /> : ''}
        </button>
        <span className="task-icon">{task.icon || '✅'}</span>
        <div style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => openEdit(task)}>
          <div className="task-title" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{task.title}</div>
          <div className="task-meta">
            {isOverdue && <span style={{ color: '#EF5350', fontWeight: 700, marginRight: 6 }}>⚠️ En retard · {new Date(task.next_due + 'T00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</span>}
            {isToday && !isOverdue && <span style={{ color: '#FF9800', marginRight: 6 }}>Aujourd'hui{task.due_time ? ` · ⏰ ${task.due_time.substring(0,5)}` : ''}</span>}
            {task.next_due && !isToday && !isOverdue && task.due_scope === 'week' && <span style={{ color: '#6C63FF', marginRight: 6 }}>📅 Cette semaine</span>}
            {task.next_due && !isToday && !isOverdue && task.due_scope === 'month' && <span style={{ color: '#8888AA', marginRight: 6 }}>📅 Ce mois-ci</span>}
            {task.next_due && !isToday && !isOverdue && !task.due_scope && <span style={{ marginRight: 6 }}>{new Date(task.next_due + 'T00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}{task.due_time ? ` · ⏰ ${task.due_time.substring(0,5)}` : ''}</span>}
            {member && <span style={{ color: member.color }}>{member.avatar_emoji} {member.name}</span>}
            {task.rotation_enabled && <span title="Rotation"> 🔄</span>}
            {task.recurrence !== 'une_fois' && <span style={{ marginLeft: 4, color: '#8888AA', fontSize: 10 }}>({RECURRENCE_LABELS[task.recurrence]})</span>}
          </div>
        </div>
        {task.priority === 'high' && <span className="badge badge-urgent" style={{ fontSize: 10 }}>!</span>}
      </div>
    );
  };

  return (
    <div>
      <div className="topbar">
        <div className="page-title">✅ Tâches</div>
        <button className="btn btn-primary btn-sm" onClick={openNew}>+ Tâche</button>
      </div>


      <div className="page" style={{ paddingTop: 0 }}>
        {/* FILTRES */}
        <div className="cat-filter">
          {[['tous','Toutes'], ['urgent','🔴 Urgentes'], ['aujourd_hui','📅 Aujourd\'hui'], ['moi','👤 Pour moi']].map(([val, label]) => (
            <button key={val} className={`cat-chip${filter === val ? ' active' : ''}`} onClick={() => setFilter(val)}>{label}</button>
          ))}
        </div>

        {overdue.length > 0 && (
          <>
            <div className="section-title" style={{ color: '#EF5350' }}>⚠️ En retard ({overdue.length})</div>
            {overdue.map(renderTask)}
          </>
        )}

        {todayTasks.length > 0 && (
          <>
            <div className="section-title">📅 Aujourd'hui ({todayTasks.length})</div>
            {todayTasks.map(renderTask)}
          </>
        )}

        {noDateTasks.length > 0 && (
          <>
            <div className="section-title">📋 À faire ({noDateTasks.length})</div>
            {noDateTasks.map(renderTask)}
          </>
        )}

        {thisWeek.length > 0 && (
          <>
            <div className="section-title">📆 Cette semaine ({thisWeek.length})</div>
            {thisWeek.map(renderTask)}
          </>
        )}

        {thisMonth.length > 0 && (
          <>
            <div className="section-title">🗓️ Ce mois ({thisMonth.length})</div>
            {thisMonth.map(renderTask)}
          </>
        )}

        {trimestre.length > 0 && (
          <>
            <div className="section-title">📋 Dans les 3 prochains mois ({trimestre.length})</div>
            {trimestre.map(renderTask)}
          </>
        )}

        {later.length > 0 && (
          <>
            <div className="section-title">📌 Plus tard ({later.length})</div>
            {later.map(renderTask)}
          </>
        )}

        {filtered.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">🎉</div>
            <div className="empty-text">Aucune tâche en attente !<br />Bravo à toute la famille !</div>
            <button className="btn btn-primary btn-sm" style={{ marginTop: 12 }} onClick={openNew}>+ Ajouter une tâche</button>
          </div>
        )}

        {/* RÉPARTITION DES TÂCHES */}
        {members.length > 1 && tasks.length > 0 && (() => {
          const maxCount = Math.max(...members.map(m => tasks.filter(t => t.assigned_to === m.id).length), 1);
          return (
            <div style={{ marginTop: 24, marginBottom: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#AAAACC', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>⚖️ Répartition</div>
              <div style={{ background: '#F8F9FF', borderRadius: 14, padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {members.map(m => {
                  const count = tasks.filter(t => t.assigned_to === m.id).length;
                  const pct = Math.round((count / maxCount) * 100);
                  return (
                    <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 18 }}>{m.avatar_emoji}</span>
                      <div style={{ width: 60, fontSize: 13, fontWeight: 600, color: '#444466' }}>{m.name}</div>
                      <div style={{ flex: 1, height: 8, background: '#E0E4F0', borderRadius: 99 }}>
                        <div style={{ height: 8, borderRadius: 99, width: `${pct}%`, background: pct > 70 ? '#EF5350' : pct > 40 ? '#FF9800' : '#4CAF50', transition: 'width 0.3s' }} />
                      </div>
                      <div style={{ width: 20, textAlign: 'right', fontSize: 13, fontWeight: 700, color: '#8888AA' }}>{count}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* TÂCHES TERMINÉES */}
        {completedTasks.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <button onClick={() => setShowCompleted(!showCompleted)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', marginBottom: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 800, color: '#AAAACC', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                ✅ Terminées ({completedTasks.length})
              </span>
              <span style={{ color: '#AAAACC', fontSize: 14 }}>{showCompleted ? '▲' : '▼'}</span>
            </button>

            {showCompleted && completedTasks.map(task => (
              <div key={task.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#F8F9FF', borderRadius: 12, marginBottom: 6, opacity: 0.75 }}>
                <span style={{ fontSize: 18 }}>{task.icon || '✅'}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#8888AA', textDecoration: 'line-through', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{task.title}</div>
                  {task.completed_at && (
                    <div style={{ fontSize: 11, color: '#BBBBCC' }}>
                      Terminée le {new Date(task.completed_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      {task.completed_by_name ? ` par ${task.completed_by_name}` : ''}
                    </div>
                  )}
                </div>
                <button onClick={() => handleUncomplete(task.id)}
                  title="Remettre à faire"
                  style={{ background: '#EEF0FF', color: '#6C63FF', border: 'none', borderRadius: 8, padding: '6px 10px', fontWeight: 700, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  ↩️ Remettre
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* MODAL */}
      {showForm && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowForm(false)}>
          <div className="modal">
            <div className="modal-handle" />
            <div className="modal-title">{editTask ? '✏️ Modifier la tâche' : '✅ Nouvelle tâche'}</div>

            <div className="form-group">
              <label className="form-label">Icône</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                {TASK_ICONS.map(ic => (
                  <button key={ic} type="button" onClick={() => setForm({ ...form, icon: ic })}
                    style={{ width: 36, height: 36, border: form.icon === ic ? '2px solid #6C63FF' : '2px solid #E0E4F0', borderRadius: 8, background: form.icon === ic ? '#EEF0FF' : '#F0F2FF', fontSize: 18, cursor: 'pointer' }}>
                    {ic}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Titre de la tâche</label>
              <input className="form-input" placeholder="Ex: Sortir les poubelles..." value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} autoFocus />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="form-group">
                <label className="form-label">Priorité</label>
                <select className="form-input" value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
                  <option value="low">🟢 Faible</option>
                  <option value="medium">🟡 Normal</option>
                  <option value="high">🔴 Urgent</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Récurrence</label>
                <select className="form-input" value={form.recurrence} onChange={e => setForm({ ...form, recurrence: e.target.value, recurrence_months: e.target.value !== 'une_fois' ? '' : form.recurrence_months })}>
                  <option value="une_fois">Une fois</option>
                  <option value="quotidienne">Chaque jour</option>
                  <option value="hebdomadaire">Chaque semaine</option>
                  <option value="bimensuelle">Toutes les 2 sem.</option>
                  <option value="mensuelle">Chaque mois</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Échéance</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                {[['no_date', '📭 Sans date'], ['', '📅 Date'], ['week', '📆 Cette sem.'], ['month', '🗓️ Ce mois']].map(([val, label]) => (
                  <button key={val} type="button"
                    onClick={() => setForm({ ...form, due_scope: val, next_due: val === 'no_date' ? '' : (val ? today : form.next_due || today), recurrence_months: '' })}
                    style={{ flex: 1, padding: '8px 6px', borderRadius: 10, border: form.due_scope === val && !form.recurrence_months ? '2px solid #6C63FF' : '2px solid #E0E4F0', background: form.due_scope === val && !form.recurrence_months ? '#EEF0FF' : '#F8F9FF', color: form.due_scope === val && !form.recurrence_months ? '#6C63FF' : '#666', fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    {label}
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {[[1,'1 mois'],[3,'3 mois'],[6,'6 mois'],[12,'1 an'],[24,'2 ans']].map(([months, label]) => {
                  const target = (() => { const d = new Date(); d.setMonth(d.getMonth() + months); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })();
                  const active = form.recurrence_months === String(months);
                  return (
                    <button key={months} type="button"
                      onClick={() => setForm({ ...form, due_scope: '', next_due: target, recurrence_months: String(months) })}
                      style={{ flex: 1, padding: '7px 4px', borderRadius: 10, border: active ? '2px solid #FF9800' : '2px solid #E0E4F0', background: active ? '#FFF3E0' : '#F8F9FF', color: active ? '#FF9800' : '#888', fontWeight: 700, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      📆 {label}
                    </button>
                  );
                })}
              </div>
            </div>

            {form.due_scope === '' && form.due_scope !== 'no_date' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div className="form-group">
                  <label className="form-label">Pour le</label>
                  <input type="date" className="form-input" value={form.next_due} onChange={e => setForm({ ...form, next_due: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">⏰ Heure (optionnel)</label>
                  <input type="time" className="form-input" value={form.due_time} onChange={e => setForm({ ...form, due_time: e.target.value })} />
                </div>
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="form-group">
                <label className="form-label">Assigné à</label>
                <select className="form-input" value={form.assigned_to} onChange={e => setForm({ ...form, assigned_to: e.target.value })}>
                  <option value="">Tout le monde</option>
                  {members.map(m => <option key={m.id} value={m.id}>{m.avatar_emoji} {m.name}</option>)}
                </select>
              </div>
            </div>

            {members.length > 1 && form.recurrence !== 'une_fois' && (
              <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input type="checkbox" id="rotation" checked={form.rotation_enabled} onChange={e => setForm({ ...form, rotation_enabled: e.target.checked })} />
                <label htmlFor="rotation" style={{ fontSize: 14, fontWeight: 600, color: '#555577', cursor: 'pointer' }}>
                  🔄 Rotation automatique entre les membres
                </label>
              </div>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              {editTask && <button className="btn btn-danger btn-sm" onClick={handleDelete} style={{ flex: 1 }}>🗑️</button>}
              <button className="btn btn-ghost" onClick={() => setShowForm(false)} style={{ flex: 1 }}>Annuler</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving} style={{ flex: 2 }}>
                {saving ? <span className="spinner" style={{ width: 16, height: 16 }} /> : '✓ Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
